import React from 'react';
import { Link, Image, useRouter } from './common/NextAdapters';
import { useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { cn } from '../lib/utils';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ProfileAvatar } from './profile-avatar';
import { SearchModal } from './search-modal';
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from './ui/sheet';
import { useAuthStore } from '../store';

const NAV = [
  { label: 'Home', href: '/' },
  { label: 'Movies', href: '/movies' },
  { label: 'Series', href: '/series' },
  { label: 'Categories', href: '/categories' },
  { label: 'Watchlist', href: '/watchlist' },
];

export function SiteHeader({ current }) {
  const location = useLocation();
  const router = useRouter();
  const activePath = current || location.pathname;

  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const isAuthenticated = !!token;
  const activeProfile = useAuthStore((s) => s.activeProfile);
  const profiles = useAuthStore((s) => s.profiles);
  const isSubscribed = useAuthStore((s) => s.hasSubscription);
  const logout = useAuthStore((s) => s.logout);
  const clearActiveProfile = useAuthStore((s) => s.clearActiveProfile);

  const [lifted, setLifted] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setLifted(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const changeProfile = () => {
    clearActiveProfile();
    router.push('/whos-watching', { state: { fromSwitch: true } });
  };

  // Profiles come back ordered by DateCreated (see ProfileController.GetProfiles), so the first
  // one is the original/main profile on the account — only it should see the admin Dashboard link.
  const isMainProfile = !activeProfile || profiles?.[0]?.Id === activeProfile.Id;
  const isCreator = user?.Role === 'ContentCreator' && !activeProfile?.IsKids;

  const userInitial = React.useMemo(() => {
    if (!user) return '';
    const name = user.FullName || user.fullName || user.Username || user.username || user.Email || user.email || '';
    return name.trim().charAt(0).toUpperCase();
  }, [user]);

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
          lifted ? 'bg-[#07050e]/90 backdrop-blur-xl border-b border-white/5' : 'bg-gradient-to-b from-black/80 to-transparent'
        )}
      >
        <div className="mx-auto flex h-16 max-w-[1600px] items-center gap-3 gutter sm:gap-6">
          {/* Mobile hamburger menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-foreground/80 hover:text-foreground lg:hidden"
                aria-label="Open menu"
              >
                <Icon icon="ph:list-bold" className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-white/10 bg-[#07050e] p-0">
              <SheetTitle className="sr-only">Browse Kast</SheetTitle>
              <div className="px-6 pt-6 pb-8">
                <Image
                  src="/logo-full.png"
                  alt="Kast"
                  width={132}
                  height={40}
                  className="h-8 w-auto"
                />
              </div>
              <nav className="flex flex-col">
                <SheetClose asChild>
                  <button
                    type="button"
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center gap-3 border-l-2 border-transparent px-6 py-3 text-left text-xl text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <Icon icon="ph:magnifying-glass-bold" className="size-5" />
                    Search
                  </button>
                </SheetClose>
                {NAV.map((item) => (
                  <SheetClose asChild key={item.href}>
                    <Link
                      href={item.href}
                      aria-current={activePath === item.href ? 'page' : undefined}
                      className={cn(
                        'border-l-2 px-6 py-3 text-xl transition-colors',
                        activePath === item.href
                          ? 'border-primary text-foreground font-semibold'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {item.label}
                    </Link>
                  </SheetClose>
                ))}
              </nav>
            </SheetContent>
          </Sheet>

          {/* Kast Logo */}
          <Link href="/" className="shrink-0 flex items-center" aria-label="Kast home">
            <Image
              src="/logo-full.png"
              alt="Kast"
              width={132}
              height={40}
              priority
              className="h-7 w-auto sm:h-8"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-1 lg:flex ml-4">
            {NAV.map((item) => {
              const active = activePath === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'relative rounded-md px-3 py-2 text-lg transition-colors',
                    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                    active ? 'text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {item.label}
                  {active && (
                    <span className="brand-sweep absolute inset-x-3 -bottom-px h-0.5 rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right Header Actions: Search, Subscribe, Account */}
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className={cn(
                'flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2.5 sm:px-3.5 text-base text-muted-foreground transition',
                'hover:border-white/25 hover:bg-white/10 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
              )}
            >
              <Icon icon="ph:magnifying-glass-bold" className="size-4 text-foreground/80" />
              <span className="hidden sm:inline">Search titles, genres…</span>
              <kbd className="hidden rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground/80 lg:inline-block">
                ⌘K
              </kbd>
            </button>

            {!isSubscribed && (
              <Button
                asChild
                size="sm"
                className="brand-sweep hidden h-8 border-0 px-3.5 text-base font-semibold text-white shadow-lg hover:opacity-90 sm:inline-flex"
              >
                <Link href="/subscribe">Subscribe</Link>
              </Button>
            )}

            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Account"
                  className="rounded-full ring-offset-background transition-transform active:scale-95"
                >
                  {isAuthenticated && activeProfile ? (
                    <ProfileAvatar
                      avatarId={activeProfile.AvatarUrl || activeProfile.avatarId}
                      className="size-8 ring-1 ring-white/15"
                      iconClassName="size-4"
                    />
                  ) : (
                    <Avatar className="size-8 ring-1 ring-white/15">
                      {isAuthenticated && userInitial ? (
                        <AvatarFallback className="brand-sweep text-xs font-bold text-white uppercase select-none">
                          {userInitial}
                        </AvatarFallback>
                      ) : (
                        <AvatarFallback className="bg-white/10 text-white hover:bg-white/15">
                          <Icon icon="ph:user-fill" className="size-4" />
                        </AvatarFallback>
                      )}
                    </Avatar>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#16121f] border-white/10">
                {isAuthenticated ? (
                  <>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex items-center gap-2.5 py-0.5">
                        {activeProfile ? (
                          <ProfileAvatar
                            avatarId={activeProfile.AvatarUrl || activeProfile.avatarId}
                            className="size-8"
                            iconClassName="size-4"
                          />
                        ) : (
                          <div className="brand-sweep flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white uppercase select-none">
                            {userInitial || 'U'}
                          </div>
                        )}
                        <div className="flex flex-col space-y-0.5 min-w-0 flex-1">
                          <p className="text-lg font-semibold text-foreground leading-none truncate">
                            {activeProfile?.Name || activeProfile?.name || user?.FullName || user?.Username || 'My Account'}
                          </p>
                          <p className="text-base text-muted-foreground leading-none truncate">
                            {user?.Email || user?.email || (user?.Username ? `@${user.Username}` : '')}
                          </p>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {user?.Role === 'Admin' && isMainProfile && (
                      <DropdownMenuItem asChild>
                        <Link href="/admin" className="cursor-pointer flex items-center">
                          <Icon icon="ph:shield-check-bold" className="mr-2 size-4 text-muted-foreground" />
                          Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isCreator && (
                      <DropdownMenuItem asChild>
                        <Link href="/creator" className="cursor-pointer flex items-center">
                          <Icon icon="ph:film-slate-bold" className="mr-2 size-4 text-muted-foreground" />
                          Creator Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={changeProfile} className="cursor-pointer">
                      <Icon icon="ph:users-three-bold" className="mr-2 size-4 text-muted-foreground" />
                      Switch profile
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/watchlist" className="cursor-pointer flex items-center">
                        <Icon icon="ph:bookmark-simple-bold" className="mr-2 size-4 text-muted-foreground" />
                        Watchlist
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/profile" className="cursor-pointer flex items-center">
                        <Icon icon="ph:gear-bold" className="mr-2 size-4 text-muted-foreground" />
                        Account settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/subscribe" className="flex items-center cursor-pointer">
                        <Icon
                          icon="ph:sparkle-bold"
                          className={`mr-2 size-4 ${isSubscribed ? 'text-emerald-400' : 'text-muted-foreground'}`}
                        />
                        <span>Subscription</span>
                        {isSubscribed && (
                          <Badge className="ml-auto bg-emerald-500/15 text-emerald-400 text-[10px] px-1.5 py-0 h-4 border-emerald-500/20">
                            Active
                          </Badge>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        logout();
                        router.push('/login');
                      }}
                      className="cursor-pointer text-destructive"
                    >
                      <Icon icon="ph:sign-out-bold" className="mr-2 size-4" />
                      Sign out
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuLabel>You are not signed in</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/login">Sign in</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/login?tab=register">Create an account</Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/subscribe">Plans and pricing</Link>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Search Modal */}
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}

export default SiteHeader;
